import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { useLang } from "../../contexts/LanguageContext";
import { useCookieConsent } from "../../contexts/CookieConsentContext";
import { api } from "../../lib/api";
import { MapPin, Phone, Mail, Clock, Facebook, Linkedin, Instagram, Youtube, Twitter } from "lucide-react";

const LOGO_DARK = "https://customer-assets.emergentagent.com/job_277e14a4-0f30-4a88-8c2a-65bd1a744cb8/artifacts/o7xpulnt_logo-horizontal-cecit-for-light-bg%20%282%29.png";

export default function Footer() {
  const { t, lang } = useLang();
  const { openPreferences } = useCookieConsent();
  const [settings, setSettings] = useState(null);
  const [menuConfig, setMenuConfig] = useState({ hidden: [], order: [] });

  useEffect(() => {
    api.get("/settings").then((r) => {
      setSettings(r.data);
      setMenuConfig(r.data?.menu_config?.footer || { hidden: [], order: [] });
    }).catch(() => {});
  }, []);

  const rawLinkCols = [
    {
      key: "footer.camara",
      title: t("nav.camara"),
      links: [
        { key: "footer.camara.overview", to: "/la-camara", label: t("nav.camaraOverview") },
        { key: "footer.camara.mision", to: "/mision-objetivos", label: t("nav.mision") },
        { key: "footer.camara.contacto", to: "/contacto", label: t("nav.contacto") },
      ],
    },
    {
      key: "footer.servicios",
      title: t("nav.servicios"),
      links: [
        { key: "footer.servicios.info", to: "/servicios/informacion-empresarial", label: t("services.info") },
        { key: "footer.servicios.training", to: "/servicios/plataforma-formacion", label: t("services.training") },
        { key: "footer.servicios.visa", to: "/servicios/gestion-visados", label: t("services.visa") },
        { key: "footer.servicios.jobs", to: "/servicios/bolsa-empleo", label: t("services.jobs") },
      ],
    },
    {
      key: "footer.socios",
      title: t("nav.socios"),
      links: [
        { key: "footer.socios.ventajas", to: "/ventajas-socio", label: t("nav.ventajas") },
        { key: "footer.socios.standard", to: "/socios-standard", label: t("nav.sociosStandard") },
        { key: "footer.socios.premium", to: "/socios-premium", label: t("nav.sociosPremium") },
        { key: "footer.socios.buscador", to: "/buscador-socios", label: t("nav.buscador") },
      ],
    },
    {
      key: "footer.informacion",
      title: t("nav.informacion"),
      links: [
        { key: "footer.informacion.anuario", to: "/anuario", label: t("nav.anuario") },
        { key: "footer.informacion.newsletters", to: "/newsletters", label: t("nav.newsletters") },
        { key: "footer.informacion.noticias", to: "/noticias-socios", label: t("nav.noticias") },
        { key: "footer.informacion.invertir", to: "/invertir-marruecos", label: t("nav.invertir") },
      ],
    },
  ];

  // Apply footer menu_config (hidden + order) same as Header does.
  const hidden = new Set(menuConfig?.hidden || []);
  const order = menuConfig?.order || [];
  const rank = new Map(order.map((k, i) => [k, i]));
  const rankOf = (k, fallbackIdx) => (rank.has(k) ? rank.get(k) : 10000 + fallbackIdx);
  const linkCols = rawLinkCols
    .filter((c) => !hidden.has(c.key))
    .map((c) => ({
      ...c,
      links: c.links
        .filter((l) => !hidden.has(l.key))
        .map((l, i) => ({ ...l, __rank: rankOf(l.key, i) }))
        .sort((a, b) => a.__rank - b.__rank),
    }))
    .map((c, i) => ({ ...c, __rank: rankOf(c.key, i) }))
    .sort((a, b) => a.__rank - b.__rank);

  const legalLinks = [
    { key: "footer.legal.privacy", to: "/politica-privacidad", label: t("footer.privacy") },
    { key: "footer.legal.cookies", to: "/politica-cookies", label: t("footer.cookies") },
    { key: "footer.legal.notice", to: "/aviso-legal", label: t("footer.legalNotice") },
  ].filter((l) => !hidden.has(l.key));

  const showContact = !hidden.has("footer.contact");
  const showSocial = !hidden.has("footer.social");
  const showPartners = !hidden.has("footer.partners");

  const socialIcon = (name, Icon, url) =>
    url ? (
      <a key={name} href={url} target="_blank" rel="noreferrer" data-testid={`footer-social-${name}`}
         className="w-9 h-9 border border-white/20 flex items-center justify-center hover:bg-cecit-red hover:border-cecit-red transition-colors">
        <Icon className="w-4 h-4" />
      </a>
    ) : null;

  return (
    <footer className="bg-[#0F2040] text-white">
      {/* Partners banner */}
      {showPartners && settings?.partner_logos?.length > 0 && (
        <div className="bg-white py-10 border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-6">
            <p className="text-center text-xs font-bold tracking-[0.25em] text-[#0F2040] mb-8 uppercase" data-testid="footer-partners-title">
              {t("footer.partnersTitle")}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-10 lg:gap-16">
              {settings.partner_logos.map((p, i) => (
                <div key={i} className="flex items-center" data-testid={`footer-partner-${i}`}>
                  <img src={p.logo_url} alt={p.name} className="h-14 md:h-16 w-auto object-contain" />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-10 mb-12">
          {linkCols.map((col) => (
            <div key={col.key}>
              <h4 className="text-xs font-semibold uppercase tracking-[0.18em] text-cecit-gold mb-4 font-sans" style={{fontFamily: "'Outfit', sans-serif"}}>
                {col.title}
              </h4>
              <ul className="space-y-2.5">
                {col.links.map((l) => (
                  <li key={l.key}>
                    <Link to={l.to} className="text-sm text-white/70 hover:text-white transition-colors">
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="border-t border-white/10 pt-10 grid grid-cols-1 md:grid-cols-3 gap-10">
          <div>
            <img src={LOGO_DARK} alt="CECIT" className="h-14 w-auto mb-4 brightness-0 invert" />
            <p className="text-xs text-white/60 leading-relaxed">
              {t("footer.about")}
            </p>
          </div>

          {showContact && settings && (
            <div className="space-y-3 text-sm">
              <h4 className="text-xs font-semibold uppercase tracking-[0.18em] text-cecit-gold mb-3" style={{fontFamily: "'Outfit', sans-serif"}}>
                {t("footer.contact")}
              </h4>
              <div className="flex items-start gap-2.5 text-white/80">
                <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-cecit-gold" />
                <span>{settings.contact_address}</span>
              </div>
              <div className="flex items-center gap-2.5 text-white/80">
                <Phone className="w-4 h-4 shrink-0 text-cecit-gold" />
                <span dir="ltr" style={{ unicodeBidi: "isolate", textAlign: "start" }}>{settings.contact_phone}</span>
              </div>
              <div className="flex items-center gap-2.5 text-white/80">
                <Mail className="w-4 h-4 shrink-0 text-cecit-gold" />
                <a href={`mailto:${settings.contact_email}`} dir="ltr" style={{ unicodeBidi: "isolate", textAlign: "start" }} className="hover:text-white">{settings.contact_email}</a>
              </div>
              <div className="flex items-start gap-2.5 text-white/80">
                <Clock className="w-4 h-4 mt-0.5 shrink-0 text-cecit-gold" />
                <span>{settings[`contact_hours_${lang}`] || settings.contact_hours_es || settings.contact_hours_fr}</span>
              </div>
            </div>
          )}

          {showSocial && (
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-[0.18em] text-cecit-gold mb-3" style={{fontFamily: "'Outfit', sans-serif"}}>
                {t("footer.social")}
              </h4>
              <div className="flex gap-3">
                {settings && socialIcon("facebook", Facebook, settings.facebook)}
                {settings && socialIcon("linkedin", Linkedin, settings.linkedin)}
                {settings && socialIcon("instagram", Instagram, settings.instagram)}
                {settings && socialIcon("twitter", Twitter, settings.twitter)}
                {settings && socialIcon("youtube", Youtube, settings.youtube)}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-5 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-white/50">
          <div className="flex flex-col md:flex-row items-center gap-1 md:gap-3">
            <p>© {new Date().getFullYear()} CECIT. {t("footer.rights")}</p>
            <span className="hidden md:inline text-white/20">·</span>
            <p data-testid="footer-credit">
              {t("footer.poweredByPrefix")}{" "}
              <a
                href="https://crescents.ma"
                target="_blank"
                rel="noopener"
                title={t("footer.webAgencyTangier")}
                className="text-white/70 hover:text-white underline-offset-2 hover:underline transition-colors"
              >
                CRESCENTS
              </a>
            </p>
          </div>
          <div className="flex gap-5 flex-wrap justify-center">
            {legalLinks.map((l) => (
              <Link key={l.key} to={l.to} className="hover:text-white">{l.label}</Link>
            ))}
            <button
              type="button"
              onClick={openPreferences}
              data-testid="footer-cookie-prefs"
              className="hover:text-white underline-offset-2 hover:underline"
            >
              {t("footer.managePrefs")}
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}
